import { ReactNode, useMemo, useState } from "react";
import * as Cesium from "cesium";
import { GenericSimulatorContext } from "../context";
import {
    ECEFState,
    ElementMap,
    FooterTab,
    LinkStatsData,
    Modals,
    ReplayState,
    SelectedElementInfo,
    TrailPoint,
    VisualizationData,
    VisualizationSettings,
} from "../types";
import { DEFAULT_VISUALIZATION_SETTINGS } from "../helpers/cesiumPlotHelpers";

const GenericSimulatorProvider = ({ children }: { children: ReactNode }) => {
    const [viewer, setViewer] = useState<Cesium.Viewer | null>(null);
    const [activeTab, setActiveTab] = useState<FooterTab>('hud');
    const [scenarioData, setScenarioData] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [deploymentData, setDeploymentData] = useState<any>(null);
    const [elementMap, setElementMap] = useState<ElementMap>({});
    const [visualizationSettings, setVisualizationSettings] = useState<VisualizationSettings>(DEFAULT_VISUALIZATION_SETTINGS);
    const [selectedElement, setSelectedElement] = useState<SelectedElementInfo | null>(null);
    const [nodeIdToElementId, setNodeIdToElementId] = useState<Record<number, string>>({});
    const [linkStatsMap, setLinkStatsMap] = useState<Record<string, LinkStatsData>>({});
    const [trailsMap, setTrailsMap] = useState<Record<string, TrailPoint[]>>({});
    const [elementHistoryMap, setElementHistoryMap] = useState<Record<string, ECEFState[]>>({});
    const [grafanaData, setGrafanaData] = useState<any>(null);
    const [visualizationData, setVisualizationData] = useState<VisualizationData | null>(null);
    const [replayState, setReplayState] = useState<ReplayState>({
        status: 'idle',
        currentTimeMs: 0,
        startTimeMs: 0,
        endTimeMs: 0,
        speed: 1,
    });
    const [modals, set_Modals] = useState<Modals[]>([]);
    const [consoleFilterOptions, setConsoleFilterOptions] = useState<any>(null);
    const [highlightedCommands, setHighlightedCommands] = useState<Set<string>>(new Set());

    const contextValue = useMemo(() => ({
        viewer,
        setViewer,
        activeTab,
        setActiveTab,
        scenarioData,
        setScenarioData,
        loading,
        setLoading,
        socket,
        setSocket,
        deploymentData,
        setDeploymentData,
        elementMap,
        setElementMap,
        visualizationSettings,
        setVisualizationSettings,
        selectedElement,
        setSelectedElement,
        nodeIdToElementId,
        setNodeIdToElementId,
        linkStatsMap,
        setLinkStatsMap,
        trailsMap,
        setTrailsMap,
        elementHistoryMap,
        setElementHistoryMap,
        grafanaData,
        setGrafanaData,
        visualizationData,
        setVisualizationData,
        replayState,
        setReplayState,
        modals,
        set_Modals,
        consoleFilterOptions,
        setConsoleFilterOptions,
        highlightedCommands,
        setHighlightedCommands,
    }), [
        viewer, activeTab, scenarioData, loading, socket, deploymentData,
        elementMap, visualizationSettings, selectedElement, nodeIdToElementId,
        linkStatsMap, trailsMap, elementHistoryMap, grafanaData,
        visualizationData, replayState, modals, consoleFilterOptions,
        highlightedCommands,
    ]);

    return (
        <GenericSimulatorContext.Provider value={contextValue}>
            {children}
        </GenericSimulatorContext.Provider>
    );
};

export default GenericSimulatorProvider;
